import React from 'react';
import { BsThreeDots } from 'react-icons/bs';

import { useGetAssociationMembers } from 'legacy/groups/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import { AssociationMemberDto } from 'legacy/groups/types';
import { Avatar } from 'components/Avatar';
import MemberList from 'components/lists/MemberList';
import MembersListItem from 'components/lists/MemberList/ListItem';
import { useTranslation } from 'react-i18next';
import DownloadCSVButton from 'components/DownloadCSVButton';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';
import { useExportAssociationMembersCSV } from 'api/queries/associations';

interface Props {
  associationId: string;
  search: string;
  setSelectedMember: (member: AssociationMemberDto) => void;
}

const MembersList = ({ associationId, search, setSelectedMember }: Props) => {
  const { t } = useTranslation(['associations']);
  const {
    isLoading,
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useGetAssociationMembers(associationId, search);

  const { isLoading: isExporting, refetch: exportCSV } =
    useExportAssociationMembersCSV({
      associationId,
      enabled: false
    });

  const onSelectMember = (member: AssociationMemberDto) => () =>
    setSelectedMember(member);

  const onClickExportCSV = async () => {
    try {
      const result = await exportCSV();
      if (result.data) {
        const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd format
        saveAs(result.data, `association-members-${today}.csv`);
      }
    } catch (error) {
      toast.error(t('members.exportMembersFailed'));
    }
  };

  return (
    <MemberList
      title={t('members.members')}
      description={t('members.membersDescription')}
      onNext={fetchNextPage}
      onPrev={fetchPreviousPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      headerContent={
        <div className="flex justify-start">
          <DownloadCSVButton onClick={onClickExportCSV} loading={isExporting} />
        </div>
      }
    >
      {isLoading ? (
        <Loader isLoading />
      ) : (
        data?.map((member) => (
          <MembersListItem
            key={member.id}
            avatar={<Avatar src={member.user.photoUrl} />}
            label={`${member.user.firstName} ${member.user.lastName}`}
            actions={
              <button
                type="button"
                aria-label="Member actions"
                onClick={onSelectMember(member)}
              >
                <BsThreeDots size={20} />
              </button>
            }
          />
        ))
      )}
    </MemberList>
  );
};

export default MembersList;
